import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon, XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

/**
 * Bases de datos alojadas DENTRO de una instancia.
 *
 * Ojo con los dos niveles: la instancia (`DatabaseInstance`) es el motor con su
 * puerto y su configuración —un SQL Server puede tener varias: MSSQLSERVER,
 * SQLEXPRESS…— y dentro de cada instancia viven las bases (`odoo_prod`,
 * `odoo_test`, `master`…). Este panel es el segundo nivel.
 */

const EMPTY = {
  name: '',
  environment: 'prod',
  size_gb: '',
  purpose: '',
  collation_name: '',
  charset: '',
  status: 'online',
  notes: '',
};

export default function InstanceDatabasesPanel({ instanceId, companyId, meta = {}, onCountChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null); // id | 'new' | null
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const environments = meta.db_environments || ['prod', 'pre', 'test', 'dev'];
  const envLabels = meta.db_environment_labels || {};
  const statuses = meta.db_statuses || ['online', 'offline', 'maintenance'];

  const load = useCallback(async () => {
    if (!instanceId) { setRows([]); return; }
    setLoading(true); setError('');
    try {
      const data = await api.getInstanceDatabases(instanceId, companyId);
      const lista = Array.isArray(data) ? data : [];
      setRows(lista);
      // El recuento lo pinta el formulario («Bases dentro»); si no se avisa,
      // el contador se queda con el valor de cuando se abrió la instancia.
      onCountChange?.(lista.length);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar las bases de datos');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceId, companyId]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setDraft(EMPTY); setEditingId('new'); setError(''); };
  const startEdit = (row) => {
    setDraft({
      name: row.name || '',
      environment: row.environment || 'prod',
      size_gb: row.size_gb ?? '',
      purpose: row.purpose || '',
      collation_name: row.collation_name || '',
      charset: row.charset || '',
      status: row.status || 'online',
      notes: row.notes || '',
    });
    setEditingId(row.id);
    setError('');
  };
  const cancel = () => { setEditingId(null); setDraft(EMPTY); setError(''); };

  const save = async () => {
    if (!draft.name.trim()) { setError('Indica el nombre de la base de datos'); return; }
    setSaving(true); setError('');
    try {
      if (editingId === 'new') {
        await api.createInstanceDatabase(instanceId, draft, companyId);
      } else {
        await api.updateInstanceDatabase(instanceId, editingId, draft, companyId);
      }
      cancel();
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo guardar');
    } finally { setSaving(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`¿Quitar la base de datos «${row.name}» de esta instancia?`)) return;
    setError('');
    try {
      await api.deleteInstanceDatabase(instanceId, row.id, companyId);
      await load();
    } catch (err) {
      setError(err.message || 'No se pudo eliminar');
    }
  };

  if (!instanceId) {
    return (
      <div className="dv-tab-pane">
        <div className="dv-section">
          <div className="dv-section-title">Bases de datos alojadas</div>
          <p style={{ fontSize: '0.86rem', color: '#64748b' }}>
            Guarda primero la instancia para poder añadir sus bases de datos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="dv-tab-pane">
      <div className="dv-section">
        <div className="dv-section-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Bases de datos alojadas ({rows.length})</span>
          <button type="button" className="dv-btn dv-btn-secondary" onClick={startNew} disabled={editingId === 'new'}>
            <PlusIcon />
            Añadir base
          </button>
        </div>

        {error && <div className="alert alert-danger" role="alert" style={{ fontSize: '0.85rem' }}>{error}</div>}

        {editingId && (
          <div style={{ border: '1px solid #cbd5e1', borderRadius: 10, padding: '0.9rem', marginBottom: '0.9rem', background: '#f8fafc' }}>
            <div className="row g-3">
              <div className="col-md-5">
                <label className="form-label">Nombre</label>
                <input
                  className="form-control"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="odoo_prod, master, corporativa…"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Entorno</label>
                <select
                  className="form-select"
                  value={draft.environment}
                  onChange={(e) => setDraft({ ...draft, environment: e.target.value })}
                >
                  {environments.map((env) => (
                    <option key={env} value={env}>{envLabels[env] || env}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label">Tamaño (GB)</label>
                <input
                  className="form-control"
                  value={draft.size_gb}
                  onChange={(e) => setDraft({ ...draft, size_gb: e.target.value })}
                  placeholder="12,5"
                />
              </div>
              <div className="col-md-2">
                <label className="form-label">Estado</label>
                <select
                  className="form-select"
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                >
                  {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Qué aplicación la usa</label>
                <input
                  className="form-control"
                  value={draft.purpose}
                  onChange={(e) => setDraft({ ...draft, purpose: e.target.value })}
                  placeholder="Odoo ERP, nóminas…"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Collation</label>
                <input
                  className="form-control"
                  value={draft.collation_name}
                  onChange={(e) => setDraft({ ...draft, collation_name: e.target.value })}
                  placeholder="Modern_Spanish_CI_AS"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Charset</label>
                <input
                  className="form-control"
                  value={draft.charset}
                  onChange={(e) => setDraft({ ...draft, charset: e.target.value })}
                  placeholder="UTF8"
                />
              </div>
              <div className="col-12">
                <label className="form-label">Notas</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
              <button type="button" className="dv-btn dv-btn-primary" onClick={save} disabled={saving}>
                {saving ? <span className="dv-spinner" /> : <CheckIcon />}
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" className="dv-btn dv-btn-secondary" onClick={cancel} disabled={saving}>
                <XMarkIcon />
                Cancelar
              </button>
            </div>
          </div>
        )}

        {loading && <div className="wol-loading"><div className="spinner-border text-primary" aria-hidden="true"></div><span>Cargando…</span></div>}

        {!loading && rows.length === 0 && !editingId && (
          <p style={{ fontSize: '0.86rem', color: '#64748b' }}>
            Esta instancia todavía no tiene bases de datos anotadas.
          </p>
        )}

        {!loading && rows.length > 0 && (
          <div className="wol-table-wrap">
            <table className="wol-table">
              <thead>
                <tr>
                  <th>Base de datos</th>
                  <th>Entorno</th>
                  <th>Tamaño</th>
                  <th>Collation</th>
                  <th>Uso</th>
                  <th>Estado</th>
                  <th style={{ textAlign: 'right' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td><strong>{row.name}</strong></td>
                    <td>{row.environment_label || row.environment}</td>
                    <td>{row.size_gb != null ? `${row.size_gb} GB` : '—'}</td>
                    <td>{row.collation_name || row.charset || '—'}</td>
                    <td>{row.purpose || '—'}</td>
                    <td>{row.status}</td>
                    <td>
                      <div className="wol-actions">
                        <button type="button" className="wol-action-btn" onClick={() => startEdit(row)} title="Editar"><PencilSquareIcon /></button>
                        <button type="button" className="wol-action-btn wol-action-btn--delete" onClick={() => remove(row)} title="Eliminar"><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
